import { Link } from 'react-router'
import { RiArrowRightLine, RiCheckLine } from 'react-icons/ri'
import { ROUTES } from '@/constants/routes'
import { useInView } from '@/hooks'

const services = [
  {
    id: 'antenatal',
    number: '01',
    title: (
      <>
        Antenatal <span className="text-crimson-700">Empowerment</span> Experience Package
      </>
    ),
    availability: 'From 24 weeks · In Person or Online',
    tagline: 'Feel calm, prepared, and truly empowered as you approach birth.',
    highlights: [
      '3 private antenatal sessions',
      'Birth partner coaching available',
      'Personalised Journey Guide included',
    ],
    from: '£495',
  },
  {
    id: 'debrief',
    number: '02',
    title: (
      <>
        Birth <span className="text-crimson-700">Healing</span> Debrief Sessions
      </>
    ),
    availability: 'From 4 weeks postnatal · In Person or Online',
    tagline: 'A safe, nurturing space to process, reflect, and begin to heal.',
    highlights: [
      '3 private 1:1 healing sessions',
      'Trauma-informed emotional support',
      'Nervous system regulation tools',
    ],
    from: '£295',
  },
  {
    id: 'postpartum',
    number: '03',
    title: (
      <>
        H.E.R <span className="text-crimson-700">Restoration</span> Postpartum Home Visits
      </>
    ),
    availability: 'Available In-Home · Essex, North Kent & London',
    tagline: 'Nourishment, rest, and gentle recovery for the early weeks.',
    highlights: [
      '3 in-home visits (3 hours each)',
      'Infant feeding & lactation support',
      'Practical & emotional care',
    ],
    from: '£495',
  },
]

export const ServicesSection = () => {
  const { ref: headerRef, inView: headerInView } = useInView<HTMLDivElement>()
  const { ref: cardsRef, inView: cardsInView } = useInView<HTMLDivElement>()
  const { ref: featuredRef, inView: featuredInView } = useInView<HTMLDivElement>()

  return (
    <section className="bg-base-50 py-20 lg:py-28 overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* ── Section header ── */}
        <div ref={headerRef} className="text-center mb-14">
          <h2
            className={`font-display font-bold text-base-900 leading-tight mb-4 ${headerInView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{
              fontSize: 'clamp(2rem, 4.5vw, 3.25rem)',
              animationDelay: headerInView ? '0.1s' : undefined,
            }}
          >
            Support Designed <em className="text-crimson-600 not-italic italic">For You</em>
          </h2>
          <div
            className={`w-12 h-[3px] rounded-full bg-gradient-to-r from-crimson-600 to-gold-500 mx-auto mb-5 ${headerInView ? 'animate-fade-in' : 'opacity-0'}`}
            style={{ animationDelay: headerInView ? '0.2s' : undefined }}
          />
          <p
            className={`text-base-600 text-[15px] leading-relaxed max-w-xl mx-auto ${headerInView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{ animationDelay: headerInView ? '0.25s' : undefined }}
          >
            Personalised birth keeper services designed to support, educate, and uplift women and
            families throughout pregnancy and postpartum.
          </p>
        </div>

        {/* ── 3-column service cards ── */}
        <div ref={cardsRef} className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
          {services.map((service, i) => (
            <div
              key={service.id}
              className={`group relative bg-white rounded-2xl border border-base-200 shadow-[0_8px_24px_-4px_rgba(155,28,28,0.12),0_2px_8px_-2px_rgba(0,0,0,0.06)] overflow-hidden flex flex-col hover:shadow-[0_20px_40px_-8px_rgba(155,28,28,0.18),0_4px_12px_-4px_rgba(0,0,0,0.08)] hover:-translate-y-1.5 transition-all duration-300 ${cardsInView ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: cardsInView ? `${i * 0.1}s` : undefined }}
            >
              <div className="h-[3px] bg-gradient-to-r from-crimson-600 to-gold-500" />

              <div className="p-7 flex flex-col flex-1 gap-5">
                {/* Number + badge */}
                <div className="flex items-start justify-between gap-3">
                  <span
                    className="font-display font-bold leading-none opacity-[0.18] text-gold-500 select-none"
                    style={{ fontSize: '3.5rem' }}
                    aria-hidden="true"
                  >
                    {service.number}
                  </span>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-crimson-600 bg-crimson-50 px-3 py-1.5 rounded-full shrink-0">
                    {service.availability.split('·')[0].trim()}
                  </span>
                </div>

                {/* Title & tagline */}
                <div className="space-y-2">
                  <h3
                    className="font-display font-bold text-base-900 leading-tight"
                    style={{ fontSize: 'clamp(1.1rem, 2vw, 1.35rem)' }}
                  >
                    {service.title}
                  </h3>
                  <p className="font-display italic text-base-500 text-[0.95rem] leading-snug">
                    {service.tagline}
                  </p>
                </div>

                {/* Highlights */}
                <ul className="space-y-2 flex-1">
                  {service.highlights.map((h) => (
                    <li key={h} className="flex items-center gap-2.5">
                      <span className="w-4 h-4 rounded-full bg-crimson-50 flex items-center justify-center shrink-0">
                        <RiCheckLine size={10} className="text-crimson-600" />
                      </span>
                      <span className="text-[13px] text-base-600">{h}</span>
                    </li>
                  ))}
                </ul>

                {/* Price + CTA */}
                <div className="flex items-center justify-between pt-2 border-t border-base-100 gap-4">
                  <div>
                    <p className="text-[10px] text-base-400 uppercase tracking-wide font-medium mb-0.5">
                      From
                    </p>
                    <p className="font-display font-bold text-gold-500 text-xl leading-none">
                      {service.from}
                    </p>
                  </div>
                  <Link
                    to={ROUTES.SERVICES}
                    className="inline-flex items-center gap-1.5 text-crimson-600 text-[13px] font-semibold group-hover:gap-2.5 transition-all duration-200"
                  >
                    Learn more
                    <RiArrowRightLine size={15} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Featured Full Journey card ── */}
        <div
          ref={featuredRef}
          className={`relative bg-crimson-50 rounded-2xl border border-gold-300 overflow-hidden shadow-[0_12px_40px_-4px_rgba(155,28,28,0.16),0_4px_12px_-4px_rgba(232,160,32,0.12)] ${featuredInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          <div className="h-[4px] bg-gradient-to-r from-gold-400 via-gold-500 to-crimson-600" />

          <div className="p-8 lg:p-10">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              {/* Left */}
              <div className="flex-1 space-y-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-crimson-600 text-[10.5px] font-bold uppercase tracking-[0.24em]">
                    Complete Package
                  </p>
                  <span className="inline-flex items-center bg-gold-500 text-base-900 text-[10px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 rounded-full">
                    Best Value — Save £135
                  </span>
                </div>
                <h3
                  className="font-display font-bold text-base-900 leading-tight"
                  style={{ fontSize: 'clamp(1.5rem, 3vw, 2.1rem)' }}
                >
                  The Full{' '}
                  <em className="text-crimson-600 not-italic italic">H.E.R Birth Journey</em>{' '}
                  Experience
                </h3>
                <p className="text-[15px] text-base-600 leading-[1.85] max-w-lg">
                  Continuous, connected support from pregnancy through to postpartum — with one
                  trusted keeper who already knows you, your preferences, and your story.
                </p>
              </div>

              {/* Right */}
              <div className="w-full lg:w-[320px] shrink-0 bg-white rounded-2xl px-6 py-6 shadow-[0_4px_14px_-4px_rgba(155,28,28,0.08)] space-y-4">
                <ul className="space-y-2.5">
                  {[
                    'Antenatal Empowerment Experience',
                    'Birth Healing Debrief Sessions',
                    'Restoration Postpartum Home Visits',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2.5">
                      <span className="w-4 h-4 rounded-full bg-crimson-50 flex items-center justify-center shrink-0">
                        <RiCheckLine size={10} className="text-crimson-600" />
                      </span>
                      <span className="text-[13px] text-base-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-2 border-t border-base-100">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="font-display font-bold text-gold-500 text-3xl leading-none">
                      £1,250
                    </span>
                    <span className="text-base-400 text-sm line-through">£1,385</span>
                  </div>
                </div>
                <Link to={ROUTES.ENQUIRY} className="btn-primary w-full justify-center">
                  Begin Your Journey
                  <RiArrowRightLine size={17} />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ── Bottom CTA ── */}
        <div
          className={`mt-10 text-center ${featuredInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: featuredInView ? '0.2s' : undefined }}
        >
          <p className="text-[12px] text-base-400">
            Not sure which service is right for you?{' '}
            <Link to={ROUTES.ENQUIRY} className="text-crimson-600 font-medium hover:underline">
              Book a free call
            </Link>{' '}
            and Simone will help you find the best fit.
          </p>
        </div>
      </div>
    </section>
  )
}
